import type { GltfJson } from '@/plugins/tiles/mesh/gltfManifest'
import { TilesError, throwIfTilesAborted } from '@/plugins/tiles/TilesError'

/** 源切片仅在调用期间借用；返回独占、紧凑的输出，共享 decoder 寿命由宿主管理。 */
export interface MeshoptDecodeRequest {
  readonly source: Uint8Array<ArrayBuffer>
  readonly count: number
  readonly byteStride: number
  readonly mode: 'ATTRIBUTES' | 'TRIANGLES' | 'INDICES'
  readonly filter: 'NONE' | 'OCTAHEDRAL' | 'QUATERNION' | 'EXPONENTIAL'
}

export type DecodeMeshopt = (
  request: MeshoptDecodeRequest,
  signal: AbortSignal
) => Promise<Uint8Array<ArrayBuffer>>

export const MESHOPT_EXTENSION = 'EXT_meshopt_compression'

function invalid(message: string): never {
  throw new TilesError({
    code: 'invalid-resource',
    stage: 'decode',
    retryable: false,
    extension: MESHOPT_EXTENSION,
    message,
  })
}

function integer(value: unknown, minimum = 0): value is number {
  return Number.isSafeInteger(value) && (value as number) >= minimum
}

function record(value: unknown): value is GltfJson {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function readExtension(view: GltfJson): GltfJson | undefined {
  const extension = view.extensions?.[MESHOPT_EXTENSION]
  if (extension !== undefined && !record(extension)) {
    invalid('Meshopt 扩展声明必须是对象')
  }
  return extension
}

function range(json: GltfJson, view: GltfJson): void {
  if (!record(view)) {
    invalid('glTF bufferView 必须是对象')
  }
  const buffer = json.buffers?.[view.buffer]
  const offset = view.byteOffset === undefined ? 0 : view.byteOffset
  if (
    !integer(view.buffer) ||
    !buffer ||
    !integer(offset) ||
    !integer(view.byteLength, 1) ||
    !integer(offset + view.byteLength) ||
    offset + view.byteLength > buffer.byteLength
  )
    invalid('glTF bufferView 范围越界')
}

/** 在预约和读取之前验证物理范围、逻辑布局及 fallback 关系。 */
export function validateMeshoptViews(json: GltfJson, hasBinary: boolean): number {
  let outputBytes = 0
  for (const name of ['buffers', 'bufferViews']) {
    if (json[name] !== undefined && !Array.isArray(json[name])) {
      invalid(`glTF ${name} 必须是数组`)
    }
  }
  for (const [index, buffer] of (json.buffers ?? []).entries()) {
    if (!record(buffer)) {
      invalid('glTF buffer 必须是对象')
    }
    const fallback = buffer.extensions?.[MESHOPT_EXTENSION]
    if (
      fallback !== undefined &&
      (fallback === null ||
        typeof fallback !== 'object' ||
        Array.isArray(fallback) ||
        (fallback.fallback !== undefined && typeof fallback.fallback !== 'boolean'))
    )
      invalid('Meshopt fallback 声明无效')
    if (
      buffer.uri === undefined &&
      !(index === 0 && hasBinary) &&
      !json.extensionsRequired?.includes(MESHOPT_EXTENSION)
    )
      invalid('无实体 buffer 要求必需 Meshopt 扩展')
  }
  for (const view of json.bufferViews ?? []) {
    range(json, view)
    const extension = readExtension(view)
    if (extension === undefined) {
      const buffer = json.buffers[view.buffer]
      if (
        buffer.extensions?.[MESHOPT_EXTENSION]?.fallback ||
        (buffer.uri === undefined && !(view.buffer === 0 && hasBinary))
      )
        invalid('普通 bufferView 引用了无实体或 fallback buffer')
      continue
    }
    range(json, extension)
    const buffer = json.buffers[extension.buffer]
    if (
      buffer.extensions?.[MESHOPT_EXTENSION]?.fallback ||
      (buffer.uri === undefined && !(extension.buffer === 0 && hasBinary))
    )
      invalid('Meshopt 压缩源必须为真实且非 fallback 的 buffer')
    const { mode, byteStride: stride, count } = extension
    const filter = extension.filter === undefined ? 'NONE' : extension.filter
    if (
      !integer(count, 1) ||
      !integer(stride, 1) ||
      !integer(count * stride, 1) ||
      count * stride !== view.byteLength ||
      (view.byteStride !== undefined && view.byteStride !== stride) ||
      !['ATTRIBUTES', 'TRIANGLES', 'INDICES'].includes(mode) ||
      !['NONE', 'OCTAHEDRAL', 'QUATERNION', 'EXPONENTIAL'].includes(filter) ||
      (mode === 'ATTRIBUTES'
        ? stride % 4 !== 0 || stride > 256
        : ![2, 4].includes(stride) || filter !== 'NONE') ||
      (mode === 'TRIANGLES' && count % 3 !== 0) ||
      (filter === 'OCTAHEDRAL' && ![4, 8].includes(stride)) ||
      (filter === 'QUATERNION' && stride !== 8) ||
      (filter === 'EXPONENTIAL' && stride % 4 !== 0)
    )
      invalid('Meshopt mode/filter/count/stride 布局无效')
    outputBytes += view.byteLength
    if (!integer(outputBytes)) invalid('Meshopt 输出总长度溢出')
  }
  return outputBytes
}

/** 内容内串行解压、按视图复用 Promise；不修改或销毁借用的 decoder。 */
export function createBufferViewResolver(
  json: GltfJson,
  buffers: readonly (Uint8Array<ArrayBuffer> | undefined)[],
  signal: AbortSignal,
  decode?: DecodeMeshopt
): { resolve(index: number): Promise<ArrayBuffer>; clear(): void } {
  const cache = new Map<number, Promise<ArrayBuffer>>()
  const outputs = new WeakSet<ArrayBuffer>()
  let tail = Promise.resolve()
  return {
    resolve(index) {
      const existing = cache.get(index)
      if (existing) return existing
      const promise = tail.then(async () => {
        throwIfTilesAborted(signal)
        const view = json.bufferViews?.[index]
        if (!record(view)) {
          invalid('glTF bufferView 引用无效')
        }
        const extension = readExtension(view)
        const definition = extension ?? view
        const buffer = buffers[definition.buffer]
        const offset = definition.byteOffset ?? 0
        if (!buffer || offset + definition.byteLength > buffer.byteLength)
          invalid('glTF bufferView 实体范围越界')
        const source = buffer.subarray(offset, offset + definition.byteLength)
        if (extension === undefined) {
          return source.slice().buffer
        }
        if (!decode) invalid('Meshopt decoder 未装配')
        const output = await Promise.resolve()
          .then(() =>
            decode(
              {
                source,
                count: extension.count,
                byteStride: extension.byteStride,
                mode: extension.mode,
                filter: extension.filter ?? 'NONE',
              },
              signal
            )
          )
          .catch((cause: unknown) => {
            throwIfTilesAborted(signal)
            throw new TilesError(
              {
                code: 'invalid-resource',
                stage: 'decode',
                retryable: false,
                extension: MESHOPT_EXTENSION,
                message: `Meshopt 解码失败: ${cause instanceof Error ? cause.message : String(cause)}`,
              },
              { cause }
            )
          })
        throwIfTilesAborted(signal)
        if (
          !(output instanceof Uint8Array) ||
          !(output.buffer instanceof ArrayBuffer) ||
          output.byteLength !== view.byteLength ||
          output.byteOffset !== 0 ||
          output.buffer.byteLength !== output.byteLength ||
          buffers.some((b) => b?.buffer === output.buffer) ||
          outputs.has(output.buffer)
        )
          invalid('Meshopt decoder 必须返回长度准确的独占紧凑输出')
        outputs.add(output.buffer)
        return output.buffer
      })
      cache.set(index, promise)
      tail = promise.then(() => undefined)
      // 失败继续传播给后续视图，避免失败内容继续分配；同时消费尾链拒绝。
      void tail.catch(() => undefined)
      return promise
    },
    clear() {
      cache.clear()
    },
  }
}
